/**
 * Screen 22 — Admin · Start Live Trivia.
 *
 * Create a live trivia (now or scheduled), then manage sessions — go-live,
 * finish, cancel. Question selection happens server-side at go-live time
 * from the approved bank.
 */

import { api, ApiError } from '../api.js';
import { auth } from '../auth.js';
import { escape, setStatus, statusZone } from '../components/form.js';
import { navigate } from '../router.js';

const STATUS_ID = 'live-admin-status';

function formatKobo(k) {
  if (!k || k <= 0) return 'Free';
  return `₦${(k / 100).toLocaleString('en-NG')}`;
}

function fmtTime(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  return d.toLocaleString('en-NG', { dateStyle: 'medium', timeStyle: 'short' });
}

function fmtCountdown(targetIso) {
  if (!targetIso) return '';
  const ms = new Date(targetIso).getTime() - Date.now();
  if (ms <= 0) return 'starting…';
  const s = Math.floor(ms / 1000);
  const d = Math.floor(s / 86400);
  const h = Math.floor((s % 86400) / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  if (d > 0) return `in ${d}d ${h}h`;
  if (h > 0) return `in ${h}h ${m}m`;
  if (m > 0) return `in ${m}m ${sec}s`;
  return `in ${sec}s`;
}

let _countdownTimer = null;
function startCountdownTicker() {
  if (_countdownTimer) clearInterval(_countdownTimer);
  _countdownTimer = setInterval(() => {
    document.querySelectorAll('[data-countdown-to]').forEach((el) => {
      el.textContent = fmtCountdown(el.dataset.countdownTo);
    });
  }, 1000);
}

export async function renderAdminLive(root) {
  const me = auth.getMe();
  if (!me?.is_admin) {
    root.innerHTML = `
      <div class="appbar"><div class="appbar-title">Live Trivia (admin)</div></div>
      <div class="pad"><div class="card" style="color:var(--color-danger)">Admins only.</div></div>
    `;
    return;
  }

  root.innerHTML = `
    <div class="appbar">
      <a href="/" data-link class="appbar-back" aria-label="Back">‹</a>
      <div class="appbar-title">Live Trivia</div>
      <div class="appbar-spacer"></div>
      <span class="status-pill bad">HOST</span>
    </div>
    <div class="pad" id="live-admin-page">
      <div class="card"><div class="muted">Loading…</div></div>
    </div>
  `;

  const page = document.getElementById('live-admin-page');
  let categories;
  let sessions;
  let adUploads;
  try {
    [categories, sessions, adUploads] = await Promise.all([
      api('/admin/categories'),
      api('/admin/live'),
      // Non-fatal: if this fails, the form just doesn't offer a reuse
      // dropdown — the URL field + file uploader still work.
      api('/admin/live/ad-uploads').catch(() => []),
    ]);
  } catch (err) {
    if (err instanceof ApiError && err.status === 401) {
      navigate('/login');
      return;
    }
    page.innerHTML = `<div class="card" style="color:var(--color-danger)">${escape(err.message)}</div>`;
    return;
  }

  page.innerHTML = `
    <div class="card">
      <h2 class="card-title">Create a live trivia</h2>
      <form id="live-form" class="form" novalidate>
        <label class="field">
          <span class="field-label">Title</span>
          <input class="field-input" name="title" type="text" required maxlength="128" placeholder="e.g. Friday Night Trivia" />
        </label>
        <label class="field">
          <span class="field-label">Categories</span>
          <select class="field-input" name="category_ids" required multiple size="${Math.min(6, Math.max(2, categories.length))}">
            ${categories.map((c) => `<option value="${escape(c.id)}">${escape(c.name)}</option>`).join('')}
          </select>
          <span class="muted" style="font-size: var(--fs-xs);">Hold ⌘/Ctrl to select multiple.</span>
        </label>
        <div class="grid cols-3">
          <label class="field">
            <span class="field-label"># questions</span>
            <input class="field-input" name="question_count" type="number" min="1" max="100" value="10" required />
          </label>
          <label class="field">
            <span class="field-label">Prize (₦)</span>
            <input class="field-input" name="prize_naira" type="number" min="0" value="0" />
          </label>
          <label class="field">
            <span class="field-label">Entry fee (₦)</span>
            <input class="field-input" name="entry_fee_naira" type="number" min="0" value="0" />
          </label>
        </div>
        <label class="field">
          <span class="field-label">Pay top N (weighted split: 80% accuracy + 20% speed)</span>
          <input class="field-input" name="payout_top_n" type="number" min="1" max="50" value="1" />
          <span class="muted" style="font-size: var(--fs-xs);">
            Pool is split across the top N by points; within that group each
            player's share = 0.8 × (their points ÷ total points) + 0.2 ×
            (fastest total time ÷ their total time). Accuracy dominates,
            speed nudges ties apart. A noticeably faster solver can edge
            ahead of a slightly higher-scoring slower one.
          </span>
        </label>
        <label class="field">
          <span class="field-label">🎁 Bonus split (% of each winner's payout paid as non-withdrawable promo credit)</span>
          <input class="field-input" name="bonus_pct" type="number" min="0" max="100" value="0" />
          <span class="muted" style="font-size: var(--fs-xs);">
            0% = all cash (legacy behaviour). Any value &gt; 0 sends that
            fraction of each winner's payout to their bonus wallet instead
            of their main wallet. Bonus is spendable on duel stakes &amp;
            power-ups only — never withdrawable. Use this to recirculate a
            slice of live winnings back into duels &amp; eraser sales.
          </span>
        </label>
        <label class="field">
          <span class="field-label">Schedule (optional — leave blank to start now)</span>
          <input class="field-input" name="scheduled_at" type="datetime-local" />
        </label>
        <label class="checkbox-row">
          <input type="checkbox" name="pinned" />
          <span>Pin to home — feature this trivia in the hero strip until it finishes.</span>
        </label>

        <h3 class="sec" style="margin-top:10px;">⏱️ Question pacing</h3>
        <div class="muted" style="font-size:11px;margin-bottom:8px;">
          Tighter windows raise the anti-cheat cost (bots need 1.5–3 s
          for OCR + LLM; humans need 3–8 s). <b>Recommendation for
          paid / prize-pool trivias: 8000–10000 ms.</b> Leave blank
          to use the defaults (15000 answer / 5000 reveal).
        </div>
        <div class="grid cols-2">
          <label class="field">
            <span class="field-label">Answer window (ms)</span>
            <input class="field-input" name="answer_window_ms" type="number" min="5000" max="30000" step="500" placeholder="15000 (default)" />
          </label>
          <label class="field">
            <span class="field-label">Reveal window (ms)</span>
            <input class="field-input" name="reveal_ms" type="number" min="2000" max="15000" step="500" placeholder="5000 (default)" />
          </label>
        </div>

        <h3 class="sec" style="margin-top:10px;">✏️ Eraser power-up</h3>
        <div class="muted" style="font-size:11px;margin-bottom:8px;">
          Players can bring erasers into the quiz to auto-correct wrong
          answers. Set 0 to disable for this trivia.
        </div>
        <div class="grid cols-2">
          <label class="field">
            <span class="field-label">Max per player</span>
            <input class="field-input" name="max_erasers_per_player" type="number" min="0" max="10" value="1" />
          </label>
          <label class="field">
            <span class="field-label">Reward count (per qualifying player)</span>
            <input class="field-input" name="eraser_reward_count" type="number" min="0" max="10" value="0" />
          </label>
        </div>
        <div class="grid cols-2">
          <label class="field">
            <span class="field-label">Reward — min rank</span>
            <input class="field-input" name="eraser_reward_min_rank" type="number" min="1" placeholder="e.g. 6" />
          </label>
          <label class="field">
            <span class="field-label">Reward — max rank</span>
            <input class="field-input" name="eraser_reward_max_rank" type="number" min="1" placeholder="e.g. 10" />
          </label>
        </div>
        <div class="muted" style="font-size:11px;margin-top:-4px;margin-bottom:8px;">
          Leave reward ranks blank to skip rank-based eraser rewards.
        </div>

        <h3 class="sec" style="margin-top:10px;">🎯 50/50 power-up</h3>
        <div class="muted" style="font-size:11px;margin-bottom:8px;">
          Tapping Use 50/50 during a question removes two wrong answers,
          leaving the correct one plus one wrong. Each use spends one
          50/50 from the player's inventory. Set max to 0 to disable.
        </div>
        <div class="grid cols-2">
          <label class="field">
            <span class="field-label">Max uses per player per quiz</span>
            <input class="field-input" name="max_fiftyfifty_per_player" type="number" min="0" max="10" value="2" />
          </label>
          <label class="field">
            <span class="field-label">Reward count (per qualifying player)</span>
            <input class="field-input" name="fiftyfifty_reward_count" type="number" min="0" max="10" value="0" />
          </label>
        </div>
        <div class="grid cols-2">
          <label class="field">
            <span class="field-label">Reward — min rank</span>
            <input class="field-input" name="fiftyfifty_reward_min_rank" type="number" min="1" placeholder="e.g. 11" />
          </label>
          <label class="field">
            <span class="field-label">Reward — max rank</span>
            <input class="field-input" name="fiftyfifty_reward_max_rank" type="number" min="1" placeholder="e.g. 20" />
          </label>
        </div>
        <div class="muted" style="font-size:11px;margin-top:-4px;margin-bottom:8px;">
          Independent of the eraser reward range — ranges can overlap or
          differ. Leave blank to skip rank-based 50/50 rewards.
        </div>

        <h3 class="sec" style="margin-top:10px;">🎬 Pre-roll ad (optional)</h3>
        <div class="muted" style="font-size:11px;margin-bottom:8px;">
          When set, the live-detail page plays this video before the Join
          CTA becomes primary. Supports YouTube, Vimeo, and direct MP4
          URLs — the player detects the kind automatically. Leave blank
          to disable.
        </div>
        <label class="field">
          <span class="field-label">Video URL</span>
          <input class="field-input" id="ad-url-input" name="ad_video_url" type="url" maxlength="500" placeholder="e.g. https://www.youtube.com/watch?v=… or https://cdn.example.com/spot.mp4" />
          <span class="muted" style="font-size: var(--fs-xs);">
            Paste a YouTube/Vimeo/MP4 URL, or upload a file below to host it on our CDN (100 MB max, MP4/WebM/MOV).
          </span>
        </label>
        <div class="row" style="gap:8px;align-items:center;margin-top:-6px;">
          <input type="file" id="ad-file-input" accept="video/mp4,video/webm,video/quicktime" style="flex:1;font-size:12px;color:var(--muted);" />
          <button type="button" id="ad-upload-btn" class="button-secondary button-inline" style="padding:6px 12px;font-size:12px;">Upload to CDN</button>
        </div>
        <div id="ad-upload-status" class="muted" style="font-size:11px;margin-top:-2px;"></div>
        ${(adUploads || []).length > 0 ? `
          <label class="field" style="margin-top:6px;">
            <span class="field-label" style="font-size:11px;">Or reuse a previous upload</span>
            <select class="field-input" id="ad-reuse-select" style="font-size:12px;">
              <option value="">— select a previously uploaded video —</option>
              ${(adUploads || []).map((u) => {
                const name = String(u.url || '').split('/').pop() || u.url;
                const usage = u.usage_count > 1 ? ` · used ×${u.usage_count}` : '';
                const dt = u.last_used_at ? new Date(u.last_used_at).toLocaleDateString() : '';
                return `<option value="${escape(u.url)}">${escape(name)}${escape(usage)}${dt ? ` · ${escape(dt)}` : ''}</option>`;
              }).join('')}
            </select>
          </label>
        ` : ''}
        <label class="field">
          <span class="field-label">Skip unlocks after (seconds)</span>
          <input class="field-input" name="ad_skippable_after_s" type="number" min="0" max="120" value="15" />
          <span class="muted" style="font-size: var(--fs-xs);">
            15 matches YouTube-style pre-roll. Set to 0 for "always skippable".
          </span>
        </label>

        ${statusZone(STATUS_ID)}
        <div class="form-actions">
          <button type="submit" class="button-gold" id="go-now-btn" data-action="now">● Go live now</button>
          <button type="button" class="button-secondary" id="schedule-btn" data-action="schedule">Schedule</button>
        </div>
      </form>
    </div>

    <h2 class="card-title" style="margin-top: var(--space-5);">Sessions</h2>
    <div id="sessions" class="card">
      ${renderSessions(sessions)}
    </div>
  `;

  document.getElementById('live-form').addEventListener('submit', (e) => {
    e.preventDefault();
    submitLive(e.currentTarget, 'now');
  });
  document.getElementById('ad-upload-btn').addEventListener('click', () => {
    void uploadAdVideo();
  });
  const reuseSel = document.getElementById('ad-reuse-select');
  if (reuseSel) {
    reuseSel.addEventListener('change', (e) => {
      const chosen = e.currentTarget.value;
      if (!chosen) return;
      document.getElementById('ad-url-input').value = chosen;
      const statusEl = document.getElementById('ad-upload-status');
      if (statusEl) {
        statusEl.style.color = 'var(--muted)';
        statusEl.textContent = 'Reusing previous upload. Click Go live to save.';
      }
    });
  }
  document.getElementById('schedule-btn').addEventListener('click', () => {
    submitLive(document.getElementById('live-form'), 'schedule');
  });
  wireSessionButtons();
  startCountdownTicker();
  // Cancel the ticker when the user leaves this route.
  window.addEventListener('popstate', () => {
    if (_countdownTimer) {
      clearInterval(_countdownTimer);
      _countdownTimer = null;
    }
  }, { once: true });
}

async function uploadAdVideo() {
  const fileEl = document.getElementById('ad-file-input');
  const urlEl = document.getElementById('ad-url-input');
  const statusEl = document.getElementById('ad-upload-status');
  const btn = document.getElementById('ad-upload-btn');
  const setStatus = (msg, isError = false) => {
    statusEl.textContent = msg || '';
    statusEl.style.color = isError ? 'var(--color-danger)' : 'var(--muted)';
  };
  const file = fileEl.files && fileEl.files[0];
  if (!file) {
    setStatus('Pick a video file first.', true);
    return;
  }
  const sizeMb = (file.size / 1024 / 1024).toFixed(1);
  setStatus(`Uploading ${sizeMb} MB…`);
  btn.disabled = true;
  const originalLabel = btn.textContent;
  btn.textContent = 'Uploading…';
  try {
    const fd = new FormData();
    fd.append('video', file);
    const res = await api('/admin/live/ad-upload', { method: 'POST', body: fd });
    // Auto-populate the URL field so the admin can just click Go live.
    urlEl.value = res.url;
    setStatus(`Uploaded ${sizeMb} MB → URL populated. Click Go live to save.`);
  } catch (err) {
    const msg = err instanceof ApiError ? err.message : 'Upload failed.';
    setStatus(msg, true);
  } finally {
    btn.disabled = false;
    btn.textContent = originalLabel;
  }
}

async function submitLive(form, mode) {
  setStatus(STATUS_ID, { message: 'Creating…', kind: 'info' });
  const formData = new FormData(form);
  const data = Object.fromEntries(formData.entries());
  const categoryIds = formData.getAll('category_ids');
  const body = {
    title: data.title,
    category_ids: categoryIds,
    question_count: Number(data.question_count),
    prize_kobo: Math.round(Number(data.prize_naira || 0) * 100),
    entry_fee_kobo: Math.round(Number(data.entry_fee_naira || 0) * 100),
    pinned: !!data.pinned,
    payout_top_n: Math.max(1, Number(data.payout_top_n || 1)),
    // Bonus split is captured as a percentage in the form (more legible
    // than basis points to a human) and converted to bps for the API.
    // Clamped to [0, 10000] both here and server-side; the round-trip is
    // safe because the form value is constrained to whole percentages.
    bonus_pct_bps: Math.max(0, Math.min(10_000, Math.round(Number(data.bonus_pct || 0) * 100))),
    max_erasers_per_player: Math.max(0, Number(data.max_erasers_per_player || 0)),
    eraser_reward_count: Math.max(0, Number(data.eraser_reward_count || 0)),
    eraser_reward_min_rank: data.eraser_reward_min_rank
      ? Number(data.eraser_reward_min_rank)
      : null,
    eraser_reward_max_rank: data.eraser_reward_max_rank
      ? Number(data.eraser_reward_max_rank)
      : null,
    max_fiftyfifty_per_player: Math.max(0, Number(data.max_fiftyfifty_per_player || 0)),
    fiftyfifty_reward_count: Math.max(0, Number(data.fiftyfifty_reward_count || 0)),
    fiftyfifty_reward_min_rank: data.fiftyfifty_reward_min_rank
      ? Number(data.fiftyfifty_reward_min_rank)
      : null,
    fiftyfifty_reward_max_rank: data.fiftyfifty_reward_max_rank
      ? Number(data.fiftyfifty_reward_max_rank)
      : null,
    ad_video_url: (data.ad_video_url || '').trim() || null,
    ad_skippable_after_s: Math.max(0, Math.min(120, Number(data.ad_skippable_after_s || 15))),
  };
  // Question pacing overrides — only include when the admin typed
  // something. Empty inputs omit the key so the server-side model
  // defaults (15000 answer / 5000 reveal) win. Pydantic bounds are
  // enforced server-side; the number inputs above declare the same
  // ranges for UX but a determined admin can still submit anything.
  const awRaw = String(data.answer_window_ms || '').trim();
  if (awRaw !== '') body.answer_window_ms = Number(awRaw);
  const rmRaw = String(data.reveal_ms || '').trim();
  if (rmRaw !== '') body.reveal_ms = Number(rmRaw);
  if (mode === 'schedule') {
    if (!data.scheduled_at) {
      setStatus(STATUS_ID, { message: 'Pick a schedule time to schedule.', kind: 'error' });
      return;
    }
    body.scheduled_at = new Date(data.scheduled_at).toISOString();
  }
  try {
    const live = await api('/admin/live', { method: 'POST', body });
    setStatus(STATUS_ID, { message: `Created — ${live.status}.`, kind: 'success' });
    renderAdminLive(document.getElementById('route-shell'));
  } catch (err) {
    const msg = err instanceof ApiError ? err.message : 'Could not create.';
    setStatus(STATUS_ID, { message: msg, kind: 'error' });
  }
}

function renderSessions(sessions) {
  if (!sessions.length) {
    return `<div class="muted">No sessions yet.</div>`;
  }
  return `
    <ul class="txn-list">
      ${sessions.map(renderSession).join('')}
    </ul>
  `;
}

function renderSession(s) {
  const pillClass =
    s.status === 'live' ? 'bad' : s.status === 'scheduled' ? 'warn' : 'ok';
  const when =
    s.status === 'live'
      ? `Live since ${fmtTime(s.started_at)}`
      : s.status === 'scheduled'
        ? `Scheduled ${fmtTime(s.scheduled_at)}`
        : `Finished ${fmtTime(s.finished_at)}`;
  // Live countdown chip — shown for scheduled trivias (counting down to
  // scheduled_at) and for any live one still in pregame (counting down to
  // started_at, which is when Q0 opens).
  const targetIso =
    s.status === 'scheduled' ? s.scheduled_at
      : (s.status === 'live' && s.started_at && new Date(s.started_at) > new Date()) ? s.started_at
      : null;
  const countdown = targetIso
    ? `<span class="status-pill warn" data-countdown-to="${escape(targetIso)}">${escape(fmtCountdown(targetIso))}</span>`
    : '';
  const actions = s.status === 'scheduled'
    ? `<button type="button" class="link-button live-action" data-action="go" data-id="${escape(s.id)}">Start now</button>`
    : s.status === 'live'
      ? `<button type="button" class="link-button live-action" data-action="finish" data-id="${escape(s.id)}">Finish</button>`
      : '';
  const cancel = s.status === 'scheduled' || s.status === 'live'
    ? `<button type="button" class="link-button danger live-action" data-action="cancel" data-id="${escape(s.id)}">Cancel</button>`
    : '';
  // Pin toggle — only meaningful while the trivia is still scheduled or live.
  const pinToggle = (s.status === 'scheduled' || s.status === 'live')
    ? `<button type="button" class="link-button live-action" data-action="${s.pinned ? 'unpin' : 'pin'}" data-id="${escape(s.id)}">${s.pinned ? '★ Unpin' : '☆ Pin'}</button>`
    : '';
  // Delete is always available (even for finished/cancelled trivias).
  const del = `<button type="button" class="link-button danger live-action" data-action="delete" data-id="${escape(s.id)}">Delete</button>`;
  // View payouts — only on finished trivias that had a prize pool.
  const viewPayouts = (s.status === 'finished' && s.prize_kobo > 0)
    ? `<button type="button" class="link-button live-action" data-action="payouts" data-id="${escape(s.id)}">💰 View payouts</button>`
    : '';
  const pinnedTag = s.pinned
    ? '<span class="status-pill warn" style="margin-right:6px;">★ PINNED</span>'
    : '';
  const topN = Number(s.payout_top_n || 1);
  // Bonus split pill — only render when the admin set a non-zero split.
  // Surfaces in the session metadata strip so it's visible without opening
  // the payouts dialog. 30% bonus on a ₦10k prize is a meaningfully
  // different game than the default 0%, and the host needs to see it.
  const bonusBps = Number(s.bonus_pct_bps || 0);
  const bonusPill = bonusBps > 0
    ? `<span class="status-pill" style="font-size:10px;padding:2px 7px;margin-left:4px;background:rgba(233,75,162,0.15);color:#E94BA2;border:1px solid rgba(233,75,162,0.45);">🎁 ${(bonusBps / 100)}% bonus</span>`
    : '';
  // Sponsor pre-roll ad — surface impressions + completions when an ad
  // is configured, so the admin can eyeball performance without opening
  // a separate reporting screen. Hidden when the trivia has no ad set.
  const impressions = Number(s.ad_impressions || 0);
  const completions = Number(s.ad_completions || 0);
  const completionPct = impressions > 0
    ? Math.round((completions / impressions) * 100)
    : 0;
  const adPill = s.ad_video_url
    ? `<span class="status-pill" title="${completions} completions / ${impressions} impressions" style="font-size:10px;padding:2px 7px;margin-left:4px;background:rgba(95,224,190,0.12);color:#5FE0BE;border:1px solid rgba(95,224,190,0.45);">🎬 ${impressions} · ${completionPct}%</span>`
    : '';
  const topics = (s.categories || []).map((c) =>
    `<span class="status-pill" style="font-size:10px;padding:2px 7px;">${escape(c.name)}</span>`
  ).join(' ');
  return `
    <li class="txn-row" style="flex-direction:column;align-items:stretch;gap:8px;">
      <div class="row" style="width:100%;">
        <div class="grow">
          <div class="txn-row-kind">${escape(s.title)}${bonusPill}${adPill}</div>
          <div class="txn-row-sub">
            ${escape(when)} · ${s.question_count} Qs ·
            ${escape(formatKobo(s.prize_kobo))} · Top ${topN} ·
            ${s.participants_count} ${s.participants_count === 1 ? 'player' : 'players'}
          </div>
          ${topics ? `<div class="row" style="gap:5px;flex-wrap:wrap;margin-top:6px;">${topics}</div>` : ''}
        </div>
        <div style="display:flex;flex-direction:column;align-items:flex-end;gap:6px;">
          <div>${pinnedTag}<span class="status-pill ${pillClass}">${escape(s.status)}</span></div>
          ${countdown}
        </div>
      </div>
      <div style="display:flex;gap:6px;flex-wrap:wrap;">${actions} ${pinToggle} ${viewPayouts} ${cancel} ${del}</div>
    </li>
  `;
}

function wireSessionButtons() {
  document.querySelectorAll('.live-action').forEach((btn) =>
    btn.addEventListener('click', async () => {
      const id = btn.dataset.id;
      const action = btn.dataset.action; // go | finish | cancel | delete
      if (action === 'cancel' && !confirm('Cancel this live trivia?')) return;
      if (action === 'delete' && !confirm('Delete this trivia? Participants & answers will be wiped.')) return;
      btn.disabled = true;
      try {
        if (action === 'delete') {
          await api(`/admin/live/${id}`, { method: 'DELETE' });
        } else if (action === 'pin' || action === 'unpin') {
          await api(`/admin/live/${id}/pin`, {
            method: 'POST',
            body: { pinned: action === 'pin' },
          });
        } else if (action === 'payouts') {
          await openPayoutsDialog(id);
          btn.disabled = false;
          return; // No re-render — the dialog overlay handles dismissal.
        } else {
          await api(`/admin/live/${id}/${action}`, { method: 'POST' });
        }
        renderAdminLive(document.getElementById('route-shell'));
      } catch (err) {
        btn.disabled = false;
        alert(err.message || 'Action failed.');
      }
    }),
  );
}

/**
 * Show the harmonic-split payout breakdown for a finished live trivia.
 * Read-only reference — nothing is debited; the operator uses this list
 * to send off-platform manual payouts (Telegram DMs, promo credit, etc).
 */
async function openPayoutsDialog(liveId) {
  const wrap = document.createElement('div');
  wrap.style.cssText = 'position:fixed;inset:0;background:rgba(0,0,0,0.6);z-index:1000;display:flex;align-items:center;justify-content:center;padding:18px;';
  wrap.innerHTML = `
    <div class="card" style="max-width:520px;width:100%;background:var(--color-surface);max-height:80vh;overflow-y:auto;">
      <div class="card-title">Payout breakdown</div>
      <p class="muted" style="font-size:var(--fs-sm);margin-bottom:10px;">
        Weighted split: 80% accuracy + 20% speed. List is sorted by payout
        descending — a fast solver can outrank a slow higher-scorer.
        Auto-shrinks when fewer players scored than top-N. Pay these
        amounts off-platform until wallet integration ships.
      </p>
      <div id="payouts-body" class="muted" style="font-size:var(--fs-sm);">Loading…</div>
      <div class="form-actions">
        <button type="button" class="button-secondary" data-action="close">Close</button>
        <button type="button" class="button-secondary" data-action="copy">Copy as text</button>
      </div>
    </div>
  `;
  document.body.appendChild(wrap);
  const close = () => wrap.remove();
  wrap.addEventListener('click', (e) => { if (e.target === wrap) close(); });
  wrap.querySelector('[data-action="close"]').addEventListener('click', close);

  let payouts = [];
  try {
    payouts = await api(`/admin/live/${liveId}/payouts`);
  } catch (err) {
    wrap.querySelector('#payouts-body').textContent = err.message || 'Could not load payouts.';
    return;
  }
  const body = wrap.querySelector('#payouts-body');
  if (!payouts.length) {
    body.textContent = 'No payouts (no qualifying scorers or prize pool is zero).';
    return;
  }
  const total = payouts.reduce((acc, p) => acc + p.payout_kobo, 0);
  const totalCash = payouts.reduce((acc, p) => acc + Number(p.cash_kobo || 0), 0);
  const totalBonus = payouts.reduce((acc, p) => acc + Number(p.bonus_kobo || 0), 0);
  const fmt = (k) => '₦' + (k / 100).toLocaleString('en-NG', { minimumFractionDigits: k % 100 ? 2 : 0 });
  // House cut breakdown — backend stamps the same values on every row, so we
  // read off the first one. Falls back to gross == total if the backend hasn't
  // sent the new fields yet (e.g. mid-deploy with a stale client cache).
  const gross = payouts[0]?.prize_gross_kobo ?? total;
  const houseCut = payouts[0]?.house_cut_kobo ?? Math.max(0, gross - total);
  const cutPct = payouts[0]?.house_cut_bps != null
    ? (payouts[0].house_cut_bps / 100).toFixed(2).replace(/\.?0+$/, '')
    : null;
  const bonusBps = Number(payouts[0]?.bonus_pct_bps || 0);
  body.innerHTML = `
    <ul class="txn-list" style="margin-bottom:10px;">
      ${payouts.map((p) => {
        const seconds = (p.total_ms / 1000).toFixed(1);
        const bonusKobo = Number(p.bonus_kobo || 0);
        const cashKobo = Number(p.cash_kobo || 0);
        // Only render the split when this trivia actually has a bonus
        // configured — keeps legacy payout dialogs visually unchanged.
        const splitLine = bonusBps > 0 && (cashKobo > 0 || bonusKobo > 0)
          ? `<div class="muted" style="font-size:11px;margin-top:2px;">
               ${escape(fmt(cashKobo))} cash · <span style="color:#E94BA2;">${escape(fmt(bonusKobo))} bonus</span>
             </div>`
          : '';
        return `
          <li class="txn-row">
            <div class="txn-row-main">
              <div class="txn-row-kind">
                <b>${p.rank}. ${escape(p.username || 'Player')}</b>
              </div>
              <div class="txn-row-sub">
                ${p.points} pts · ${p.correct} correct · ${seconds}s
              </div>
              ${splitLine}
            </div>
            <div class="txn-row-side">
              <b>${escape(fmt(p.payout_kobo))}</b>
            </div>
          </li>
        `;
      }).join('')}
    </ul>
    <div style="font-size:var(--fs-sm);display:grid;gap:3px;">
      <div><b>Prize pool:</b> ${escape(fmt(gross))}</div>
      <div><b>Distributed:</b> ${escape(fmt(total))}</div>
      <div class="muted">House cut${cutPct ? ` (${cutPct}%)` : ''}: ${escape(fmt(houseCut))}</div>
      ${bonusBps > 0
        ? `<div style="margin-top:4px;padding-top:4px;border-top:1px dashed var(--border);">
             <b>Cash paid:</b> ${escape(fmt(totalCash))}
           </div>
           <div style="color:#E94BA2;">
             <b>Bonus paid (${(bonusBps / 100)}%):</b> ${escape(fmt(totalBonus))}
           </div>`
        : ''}
    </div>
  `;
  wrap.querySelector('[data-action="copy"]').addEventListener('click', () => {
    const txt = payouts.map((p) =>
      `${p.rank}. ${p.username || 'Player'} — ${p.points} pts — ${fmt(p.payout_kobo)}`
    ).join('\n') + `\nTotal: ${fmt(total)}`;
    navigator.clipboard.writeText(txt).then(
      () => { const btn = wrap.querySelector('[data-action="copy"]'); btn.textContent = 'Copied ✓'; setTimeout(close, 600); },
      () => alert('Copy failed; pick the text manually.'),
    );
  });
}
